import { Box } from '@material-ui/core';
import {
  CheckCircleOutlineOutlined,
  EditOutlined,
  PublicOutlined,
  PublishOutlined
} from '@material-ui/icons';
import toggleNotification from 'actions/notifications';
import {
  createPublishFolder,
  fetchPublishFolderByName,
  updatePublishFolderStories
} from 'actions/publishFolders';
import {
  createStoryPool,
  fetchStoryPoolByName,
  updateStoryPoolStories
} from 'actions/storyPools';
import {
  deleteVersion,
  fetchVersionsForStory,
  updateVersion
} from 'actions/version';
import { AoVersionEditorIframe } from 'components/AlphaOmega/VersionEditor';
import {
  Action,
  ProtectedDeleteDialog,
  ProtectedIconButton,
  Resource
} from 'components/Authorization';
import Badge, { BadgeColour } from 'components/Badge';
import ContentBox from 'components/ContentBox';
import ContentBoxItem from 'components/ContentBoxItem';
import Search from 'components/Search';
import TableBuilder from 'components/Table';
import TitleWrapper from 'components/TitleWrapper';
import i18n from 'i18n';
import { Version } from 'interface';
import { VersionState } from 'interfaces/version';
import { createStory } from 'pages/stories/actions';
import { StoryFormData } from 'pages/stories/interface';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import shortid from 'shortid';

interface VersionListI {
  formData: StoryFormData;
  changeTabValue: (val: number) => void;
}

const VersionsTable: FunctionComponent<VersionListI> = ({
  formData,
  changeTabValue
}: VersionListI) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { storyStates } = useSelector(
    (state: $Lns.DefaultState) => state.default
  );

  const [versions, setVersions] = useState<$Lns.Version[]>([]);
  const [isAOModalOpen, setIsAOModalOpen] = useState(false);

  const [editVersion, setEditVersion] = useState<$Lns.Version | undefined>(
    undefined
  );

  useEffect(() => {
    if (formData.guid) {
      fetchVersionsForStory(formData.guid)
        .then(({ data }) => {
          setVersions(data || []);
        })
        .catch(() =>
          dispatch(toggleNotification('Failed to load versions', 'error'))
        );
    }
  }, [dispatch, formData.guid]);

  const getStateClass = (state: string): BadgeColour => {
    if (!state) return 'grayBlue';

    switch (state) {
      case 'Draft':
        return 'grayBlue';
      case 'Submitted':
        return 'orange';
      case 'Approved':
        return 'green';
      case 'Published':
        return 'blue';
      default:
        return 'grayBlue';
    }
  };

  const rows = [
    i18n.t('pages.stories.tableRow.pos'),
    'Operator',
    'Title',
    'Slug',
    'State',
    ''
  ];

  const buildActionBtns = (version: $Lns.Version) => {
    const buttons = [];
    if (version.state !== 'Published') {
      buttons.push(
        <ProtectedIconButton
          key={shortid()}
          size="small"
          tooltip="Edit"
          aria-label="edit"
          lnsResource={Resource.VERSION}
          lnsAction={Action.EDIT}
          onClick={() => openEdit(version)}
        >
          <EditOutlined color="secondary" />
        </ProtectedIconButton>
      );

      if (version.state === 'Draft') {
        buttons.push(
          <ProtectedIconButton
            key={shortid()}
            size="small"
            tooltip="Submit for Review"
            aria-label="submit"
            onClick={() => stateChange(version, 'Submitted')}
            lnsResource={Resource.VERSION}
            lnsAction={Action.EDIT}
          >
            <PublishOutlined color="secondary" />
          </ProtectedIconButton>
        );
      }

      if (version.state === 'Draft' || version.state === 'Submitted') {
        buttons.push(
          <ProtectedIconButton
            key={shortid()}
            size="small"
            tooltip="Review for Approval"
            aria-label="review"
            onClick={() => stateChange(version, 'Approved')}
            lnsResource={Resource.VERSION}
            lnsAction={Action.EDIT}
          >
            <CheckCircleOutlineOutlined color="secondary" />
          </ProtectedIconButton>
        );
      }

      buttons.push(
        <ProtectedIconButton
          key={shortid()}
          size="small"
          tooltip="Publish Story"
          aria-label="publish"
          onClick={() => onPublish(version)}
          lnsResource={Resource.VERSION}
          lnsAction={Action.EDIT}
        >
          <PublicOutlined color="secondary" />
        </ProtectedIconButton>
      );

      buttons.push(
        <ProtectedDeleteDialog
          key={shortid()}
          size="small"
          onDelete={() => onDelete(version.guid)}
          target={version?.title}
          targetDeleteTitle="Delete Story"
          targetDeletePrompt="Are you sure?"
          targetDeleteWarning="Cannot be undone"
          btnDisabled={false}
          lnsResource={Resource.VERSION}
        />
      );
    } else {
      buttons.push(
        <ProtectedIconButton
          key={shortid()}
          size="small"
          tooltip="View Published Story"
          aria-label="view"
          onClick={() => gotoPublished(version)}
          lnsResource={Resource.STORY}
          lnsAction={Action.READ}
        >
          <PublicOutlined color="secondary" />
        </ProtectedIconButton>
      );
    }

    return buttons;
  };

  const cells = versions.map((version, index) => ({
    fields: {
      pos: { value: index },
      guid: { value: version.guid, hidden: true },
      operatorId: { value: version.operatorId, hidden: true },
      operator: {
        value: `${
          version.operatorCategory ? `${version.operatorCategory} - ` : ''
        }${version.operatorName}`
      },
      title: { value: version.title },
      slug: { value: version.slug },
      state: {
        value: (
          <Badge
            label={version.state}
            color={getStateClass(version.state)}
            icon="dot"
            capitalize
          />
        )
      },
      actions: {
        value: <Box display="flex">{buildActionBtns(version)} </Box>
      }
    }
  }));

  function openEdit(version: Version) {
    setEditVersion(version);
    setIsAOModalOpen(true);
  }

  const onVersionSubmit = (version: Version) =>
    updateVersion({ ...editVersion, ...version, story: formData.guid })
      .then(() => {
        if (formData.guid) {
          fetchVersionsForStory(formData.guid).then(({ data }) => {
            setVersions(data || []);
          });
        }

        dispatch(
          toggleNotification(
            i18n.t('notifications.versions.updateSuccess'),
            'success'
          )
        );

        setIsAOModalOpen(false);
      })
      .catch(() =>
        dispatch(
          toggleNotification(i18n.t('notifications.apiError'), 'Update Error')
        )
      );

  const stateChange = (version: Version, state: VersionState) =>
    updateVersion({ ...version, state, story: formData.guid })
      .then(() => {
        if (formData.guid) {
          fetchVersionsForStory(formData.guid).then(({ data }) => {
            setVersions(data || []);
          });
        }

        dispatch(
          toggleNotification(
            i18n.t('notifications.versions.updateSuccess'),
            'success'
          )
        );
      })
      .catch(() =>
        dispatch(
          toggleNotification(
            i18n.t('notifications.apiError'),
            'State Change Error'
          )
        )
      );

  const onPublish = async (version: Version) => {
    await createStory({
      text: version.text ?? '',
      title: version.title,
      slug: version.slug,
      type: formData.type,
      genre: formData.genre,
      location: formData.location,
      notes: '',
      script: '',
      plannedDuration: 0,
      calculatedDuration: 0,
      storyState: storyStates.length
        ? storyStates.find(state => state.name.toLowerCase() === 'approved')
            ?.guid ?? storyStates[0].guid
        : '',
      errors: {
        title: '',
        slug: '',
        type: ''
      }
    })
      .then(({ data: newStory }) => {
        updateVersion({
          ...version,
          story: formData.guid,
          publishStory: newStory.guid,
          state: 'Published'
        })
          .then(() => {
            if (formData.guid) {
              fetchVersionsForStory(formData.guid).then(({ data }) => {
                setVersions(data || []);
              });
            }

            if (version.operatorCategory.toLowerCase() === 'digital') {
              addStoryToPublishFolder(version, newStory);
            }

            if (version.operatorCategory.toLowerCase() === 'script') {
              addStoryToStoryPool(version, newStory);
            }
          })
          .catch(() =>
            dispatch(
              toggleNotification(
                i18n.t('notifications.apiError'),
                'Publish Version Error'
              )
            )
          );
      })
      .catch(() => {
        dispatch(
          toggleNotification(
            i18n.t('notifications.apiError'),
            'Publish Version Create Story Error'
          )
        );
        return { data: null };
      });
  };

  const onDelete = (guid?: string) => {
    if (!guid) {
      dispatch(
        toggleNotification(
          i18n.t('notifications.apiError'),
          'Delete Error: Unknown GUID'
        )
      );
      return;
    }

    deleteVersion(guid)
      .then(() => {
        if (formData.guid) {
          fetchVersionsForStory(formData.guid).then(({ data }) => {
            setVersions(data || []);
          });
        }

        dispatch(
          toggleNotification(
            i18n.t('notifications.versions.deleteSuccess'),
            'success'
          )
        );
      })
      .catch(() =>
        dispatch(
          toggleNotification(i18n.t('notifications.apiError'), 'Delete Error')
        )
      );
  };

  const gotoPublished = (version: $Lns.Version) => {
    navigate(`/my-lns/${version.publishStory}/edit`);
    changeTabValue(0);
  };

  async function addStoryToPublishFolder(
    version: $Lns.Version,
    story: $Lns.Story
  ) {
    let publishFolder: void | { code: number; data: $Lns.PublishFolder };

    try {
      publishFolder = await fetchPublishFolderByName(version.operatorName);

      if (!publishFolder || !publishFolder.data) {
        publishFolder = await createPublishFolder({
          name: version.operatorName
        } as $Lns.PublishFolder);
      }

      if (publishFolder && publishFolder.data && publishFolder.code < 300) {
        const publishFolders = publishFolder.data.stories
          ? [...publishFolder.data.stories, story]
          : [story];

        await updatePublishFolderStories(
          publishFolder.data.guid,
          publishFolders
        );

        dispatch(
          toggleNotification(
            i18n.t('notifications.publishFolders.updateSuccess'),
            'success'
          )
        );
      }
    } catch (error) {
      dispatch(toggleNotification(i18n.t('notifications.apiError'), 'error'));
    }
  }

  async function addStoryToStoryPool(version: $Lns.Version, story: $Lns.Story) {
    let pool: void | { code: number; data: $Lns.StoryPool };

    try {
      pool = await fetchStoryPoolByName(version.operatorName);

      if (!pool || !pool.data) {
        pool = await createStoryPool({
          name: version.operatorName
        } as $Lns.StoryPool);
      }

      if (pool && pool.data && pool.code < 300) {
        const storyGuids = pool.data.stories
          ? [
              ...pool.data.stories.map(item => ({ story: item.guid })),
              { story: story.guid }
            ]
          : [{ story: story.guid }];

        await updateStoryPoolStories({
          pool: pool.data.guid,
          stories: storyGuids
        });

        dispatch(
          toggleNotification(
            i18n.t('notifications.storyPools.updateSuccess'),
            'success'
          )
        );
      }
    } catch (error) {
      dispatch(toggleNotification(i18n.t('notifications.apiError'), 'error'));
    }
  }

  return (
    <ContentBox>
      <AoVersionEditorIframe
        show={isAOModalOpen}
        version={editVersion}
        showCallback={setIsAOModalOpen}
        submitCallback={onVersionSubmit}
      />
      <ContentBoxItem title>
        <TitleWrapper title={i18n.t('pages.stories.tabLabel.versions')} />
      </ContentBoxItem>

      <ContentBoxItem title>
        <div />
        <Search
          fields={versions}
          searchKeys={['operator', 'language', 'title', 'slug']}
          filterFields={setVersions}
        />
      </ContentBoxItem>

      <TableBuilder rows={rows} cells={cells} />
    </ContentBox>
  );
};

export default VersionsTable;
